// 左側「組織樹 / 檢視 / 群組」面板（SPEC #org-tree-ui 第 1 點、o01 / o02 / o03 / o04 / o19 / o20 / o27 / o32）：即時影像、回放、事件、地圖共用。
//   - 三個分頁，記住上次選擇；組織樹按需載入 GET /v1/me/tree；數字 = 我看得到的（device_count，tooltip「你可存取的設備數」）
//   - 分組：我自己的 → 組織授權給我 → 別人分享給我；委派寫「委派：<委派方>」（委派根節點加「（整個組織）」），⤳ 滑過寫委派方與期限
//   - 搜尋：至少 2 字、伺服器搜（?q=），每筆「名稱 + 路徑」，最多 50 筆，more → 「還有更多，請縮小範圍」；點了展開到那一層
//   - 委派方要求兩步驟驗證、這次登入沒有：委派照樣出現、不展開，下一行 🔒 +〔設定兩步驟驗證〕
//   - 委派結束：保留 24 小時、灰字「已結束」，下一行「<委派方> 已於 … 結束委派」
//   - 受託組織底下沒有任何可見項目：「你在 <組織> 還沒有被指派任何節點，請洽 <組織> 的管理員」
//   - 點節點 = 範圍切到那個子樹；家戶展開列設備（可拖到影像牆）；節點可拖到影像牆（依版面自動分頁）
//   - 〔⋯〕：〔通知我這個節點〕（已訂閱打勾，再按取消）、〔加到檢視…〕、〔加到群組…〕、〔在影像牆開啟〕
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDismiss } from '../../components/useDismiss';
import { camDot, camState, isLiveOffline, tileState, useLiveOfflineVersion } from '../../lib/liveStatus';
import { useScope } from '../../lib/scope';
import { meTreeApi, type MeTreeItem, type ScopeSubscription } from '../../lib/orgTreeApi';
import { levelText } from '../../lib/levels';
import { topGroupOf } from '../../lib/orgTreeUi';
import { scopeKeyOf } from '../../lib/remoteScope';
import { toggle } from './DelegatedNodePicker';
import { AddToDialog } from './AddToDialog';
import { ViewsTab, GroupsTab } from './ViewsTab';

import { DND_CAM, DND_SCOPE } from './dnd';
import { placeCamera } from './placeCamera';
import { useDevices } from '../../lib/useDevices';
import { onTreeReveal, takePendingReveal } from './treeReveal';
import { dateStamp, stamp } from '../../lib/timefmt';

type Tab = 'tree' | 'views' | 'groups';
const TAB_KEY = 'kinio.vms.treeTab';

function savedTab(): Tab {
  try {
    const v = localStorage.getItem(TAB_KEY);
    return v === 'views' || v === 'groups' ? v : 'tree';
  } catch {
    return 'tree';
  }
}

type Item = MeTreeItem & { _api: string; _org?: string };

const COLLAPSE_KEY = 'kinio.vms.treeCollapsed';

/** 桌面可以收起面板，讓影像牆 / 時間軸寬一點（記住；收起後頂列〔範圍〕打開）。
 *  地圖頁與回放頁各自記（VMS UX 評審第三輪 R3-P1-6、B3-12）：地圖頁預設收合（地圖自己有左欄）；回放頁在視窗寬 < 1400 px 時預設收合（時間軸要在第一屏）。 */
export type TreePage = 'map' | 'playback' | '';

export function treeDefaultCollapsed(page: TreePage, width = typeof window === 'undefined' ? 1920 : window.innerWidth): boolean {
  if (page === 'map') return true;
  if (page === 'playback') return width < 1400;
  return false;
}

export function savedCollapsed(page: TreePage = ''): boolean {
  try {
    const v = localStorage.getItem(page ? `${COLLAPSE_KEY}.${page}` : COLLAPSE_KEY);
    return v === null ? treeDefaultCollapsed(page) : v === '1';
  } catch {
    return treeDefaultCollapsed(page);
  }
}

export function saveCollapsed(v: boolean, page: TreePage = ''): void {
  try {
    localStorage.setItem(page ? `${COLLAPSE_KEY}.${page}` : COLLAPSE_KEY, v ? '1' : '0');
  } catch {
    /* 無 storage */
  }
}

export function TreePanel({ onPicked, onCollapse }: { onPicked?(): void; onCollapse?(): void }) {
  const { t } = useTranslation();
  const [tab, setTabState] = useState<Tab>(savedTab);
  // 影像牆〔管理版面…〕→ 切到「檢視」分頁（第三輪 R3-P1-9）
  useEffect(() => {
    const on = (e: Event) => {
      const x = (e as CustomEvent<Tab>).detail;
      if (x === 'views' || x === 'groups' || x === 'tree') setTab(x);
    };
    window.addEventListener('kinio-open-tree-tab', on);
    return () => window.removeEventListener('kinio-open-tree-tab', on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 使用者從頁首切了範圍：左欄回到「組織樹」分頁，看得到新範圍在樹上哪裡（第四輪第 15 列：停在版面分頁、切了範圍也不回樹）
  // （在版面 / 群組分頁點了版面或群組本身也會切範圍——那種不跳回樹）
  const { scopeSeq, scope: curScope } = useScope();
  const seq0 = useRef(scopeSeq);
  useEffect(() => {
    if (seq0.current === scopeSeq) return;
    seq0.current = scopeSeq;
    if (!/^(view|group):/.test(curScope)) setTab('tree');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeSeq]);
  const setTab = (x: Tab) => {
    setTabState(x);
    try {
      localStorage.setItem(TAB_KEY, x);
    } catch {
      /* 無 storage */
    }
  };
  return (
    <aside className="ot-panel" data-testid="tree-panel">
      <div className="ot-tabs" role="tablist">
        {(['tree', 'views', 'groups'] as Tab[]).map((x) => (
          <button key={x} role="tab" aria-selected={tab === x} className={tab === x ? 'on' : ''} onClick={() => setTab(x)}>
            {t('ot.tab_' + x)}
          </button>
        ))}
        {onCollapse && (
          <button className="icon-btn ot-collapse-panel" aria-label={t('ot.panel_collapse')} title={t('ot.panel_collapse')} onClick={onCollapse}>
            «
          </button>
        )}
      </div>
      {tab === 'tree' && <OrgTreeTab onPicked={onPicked} />}
      {tab === 'views' && <ViewsTab onPicked={onPicked} />}
      {tab === 'groups' && <GroupsTab onPicked={onPicked} />}
    </aside>
  );
}

function OrgTreeTab({ onPicked }: { onPicked?(): void }) {
  const { t } = useTranslation();
  const { planes, scope, setScope, orgs } = useScope();
  const owned = useMemo(() => new Set(orgs.filter((o) => o.role === 'owner').map((o) => o.org_id)), [orgs]);
  const qc = useQueryClient();
  const tops = useQueries({
    queries: planes.map((p) => ({ queryKey: ['me-tree', 'level', p.api, ''], queryFn: () => meTreeApi.level(p), staleTime: 30_000, retry: 1 })),
  });
  const items: Item[] = useMemo(() => tops.flatMap((q, i) => (q.data ?? []).map((x) => ({ ...x, _api: planes[i].api }))), [tops, planes]);
  const subs = useQuery({ queryKey: ['me-tree', 'subs', planes[0]?.api], queryFn: () => meTreeApi.subscriptions(planes[0]), enabled: planes.length > 0, retry: false });
  const [open, setOpen] = useState<Set<string>>(() => new Set(takePendingReveal()));
  // 頁首範圍切換器選了組織樹的節點 → 展開到那一層（SPEC #org-tree-ui 第 1 點：兩邊同一個範圍、切了另一邊跟著展開並高亮）
  useEffect(() => onTreeReveal((keys) => setOpen((s) => new Set([...s, ...keys]))), []);
  const { homes, data, scopeLabel } = useScope();
  useEffect(() => {
    const reveal = (keys: string[]) => keys.length && setOpen((s) => (keys.every((k) => s.has(k)) ? s : new Set([...s, ...keys])));
    // 家戶 / 場域：先展開它的組織；更深的層用伺服器搜尋（同一個 /v1/me/tree?q=）找出路徑，祖先全部展開
    // （第二輪 B2-02：頁首切到左樹還沒展開的 LivingStyle › lstw，左樹要跟著展開並高亮）
    let target: { orgId?: string; name?: string; match: (x: MeTreeItem) => boolean } | null = null;
    if (scope.startsWith('home:')) {
      const h = homes.find((x) => x.home_id === scope.slice(5));
      target = { orgId: h?.org_id, name: h?.display_name, match: (x) => x.kind === 'home' && (x.home_id ?? x.id) === h?.home_id };
    } else if (scope.startsWith('site:')) {
      const sid = scope.slice(5);
      const o = data.orgs.find((x) => x.sites.some((st) => st.site_id === sid));
      target = { orgId: o?.org_id, name: o?.sites.find((st) => st.site_id === sid)?.name, match: (x) => x.kind === 'node' && x.id === sid };
    }
    else if (scope.startsWith('org:')) target = { orgId: scope.slice(4), match: () => false };
    else if (scope.startsWith('node:')) {
      // 組織樹節點範圍（node:<org>:<id>）：進即時影像 / 事件頁時也展開到它（第三輪 R3-P2-1）
      const [, orgId, nid] = scope.split(':');
      target = { orgId, name: scopeLabel.split(' › ').pop(), match: (x) => x.kind === 'node' && x.id === nid };
    }
    if (!target) return;
    const orgRow = target.orgId ? items.find((x) => x.kind === 'org' && x.id === target.orgId) : undefined;
    if (orgRow) reveal([orgRow.key]);
    const name = target.name;
    if (!name || name.length < 2) return;
    let alive = true;
    void Promise.all(planes.map((p) => meTreeApi.tree(p, { q: name, limit: 50 }).catch(() => null))).then((rs) => {
      if (!alive) return;
      const hit = rs.flatMap((r) => r?.items ?? []).find(target.match);
      if (hit) reveal((hit.path ?? []).map((x) => x.key));
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, items.length]);
  const [q, setQ] = useState('');
  const [sq, setSq] = useState('');
  const search = useQuery({
    queryKey: ['me-tree', 'q', sq],
    queryFn: async () => {
      const rs = await Promise.all(planes.map((p) => meTreeApi.tree(p, { q: sq, limit: 50 }).then((r) => ({ ...r, items: r.items.map((x) => ({ ...x, _api: p.api })) }))));
      return { items: rs.flatMap((r) => r.items) as Item[], more: rs.some((r) => r.more) };
    },
    enabled: sq.length >= 2,
  });
  const [addTo, setAddTo] = useState<{ kind: 'view' | 'group'; item: Item } | null>(null);

  const pick = (it: Item, label: string) => {
    setScope(scopeKeyOf(it, it._org ?? orgIdOfKey(it.key)), label);
    onPicked?.();
  };

  const groups: { g: 'own' | 'org' | 'shared'; items: Item[] }[] = (['own', 'org', 'shared'] as const)
    .map((g) => ({ g, items: items.filter((x) => (x.kind === 'org' || x.kind === 'sharer') && topGroupOf(x, owned) === g) }))
    .filter((x) => x.items.length);
  // 頂層的委派項掛在受託組織底下
  const dlgsOf = (orgKey: string) => items.filter((x) => x.kind === 'delegation' && x.parent_key === orgKey);

  const subscribed = (it: Item): ScopeSubscription | undefined =>
    (subs.data?.nodes ?? []).find((s) => s.node_id === it.id && (s.delegation_id ?? '') === (it.delegation?.delegation_id ?? ''));
  async function toggleSub(it: Item) {
    const p = { api: it._api };
    const target = { node_id: it.id, delegation_id: it.delegation?.delegation_id };
    if (subscribed(it)) await meTreeApi.unsubscribe(p, target);
    else await meTreeApi.subscribe(p, target);
    void qc.invalidateQueries({ queryKey: ['me-tree', 'subs'] });
  }

  const loading = tops.some((x) => x.isLoading);
  return (
    <div className="ot-tree-tab">
      <form
        className="ot-search"
        onSubmit={(e) => {
          e.preventDefault();
          setSq(q.trim());
        }}
      >
        <input
          className="input"
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            if (e.target.value.trim().length < 2) setSq('');
          }}
          placeholder={t('ot.tree_search')}
          aria-label={t('ot.tree_search')}
        />
      </form>
      {q.trim().length === 1 && <p className="muted small">{t('ot.search_min')}</p>}
      {sq.length >= 2 ? (
        <ul className="plain-list ot-results" data-testid="tree-results">
          {search.isLoading && <li className="muted small">{t('common.loading')}</li>}
          {search.data && search.data.items.length === 0 && <li className="muted small">{t('ot.no_results')}</li>}
          {(search.data?.items ?? []).map((it) => {
            const path = (it.path ?? []).map((p) => p.name).join(' › ');
            return (
              <li key={it.key}>
                <button
                  className="ot-result"
                  onClick={() => {
                    setOpen((s) => new Set([...s, ...(it.path ?? []).map((p) => p.key)]));
                    pick(it, [path, it.name].filter(Boolean).join(' › '));
                    setSq('');
                    setQ('');
                  }}
                >
                  <span>{it.name}</span>
                  {it.level_label && <span className="ot-chip">{levelText(t, it.level_label)}</span>}
                  <span className="muted small ot-path">{path}</span>
                </button>
              </li>
            );
          })}
          {search.data?.more && <li className="muted small">{t('ot.search_more')}</li>}
        </ul>
      ) : (
        <ul className="ot-tree" role="tree" aria-label={t('ot.tab_tree')}>
          {loading && <li className="muted small">{t('common.loading')}</li>}
          {!loading && items.length === 0 && <li className="muted small">{t('ot.tree_empty')}</li>}
          {groups.map(({ g, items: gi }) => (
            <li key={g} className="ot-group" role="none">
              <div className="ot-group-title">{t('scope.src_' + g)}</div>
              <ul role="group">
                {gi.map((it) => (
                  <Row
                    key={it.key}
                    it={it}
                    depth={0}
                    open={open}
                    setOpen={setOpen}
                    selected={scope}
                    pick={pick}
                    extra={it.kind === 'org' ? dlgsOf(it.key) : []}
                    subscribed={subscribed}
                    toggleSub={toggleSub}
                    addTo={(kind, item) => setAddTo({ kind, item })}
                    parentPath={[]}
                  />
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {addTo && <AddToDialog kind={addTo.kind} item={addTo.item} close={() => setAddTo(null)} />}
    </div>
  );
}

/** key 是不透明字串（arch-2-15）；只有組織項自己的 id 用得到。 */
function orgIdOfKey(key: string): string | undefined {
  return key.startsWith('org:') ? key.slice(4) : undefined;
}

function Row(props: {
  it: Item;
  depth: number;
  open: Set<string>;
  setOpen(f: (s: Set<string>) => Set<string>): void;
  selected: string;
  pick(it: Item, label: string): void;
  extra: Item[];
  subscribed(it: Item): ScopeSubscription | undefined;
  toggleSub(it: Item): Promise<void>;
  addTo(kind: 'view' | 'group', it: Item): void;
  parentPath: string[];
}) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { it, depth, open, setOpen } = props;
  const isOpen = open.has(it.key);
  const ended = it.kind === 'delegation' && it.status === 'ended';
  const mfa = it.delegation?.notice === 'delegator_mfa_required';
  const canOpen = !ended && !mfa && (it.has_children !== false || props.extra.length > 0 || it.kind === 'home');
  const name =
    it.kind === 'delegation'
      ? it.delegation?.whole_org
        ? t('ot.dlg_label_whole', { from: it.delegation?.from_org_name ?? it.name })
        : t('ot.dlg_label', { from: it.delegation?.from_org_name ?? it.name })
      : it.name;
  const label = [...props.parentPath, name].join(' › ');
  const key = scopeKeyOf(it, it._org);
  // 頁首選的場域是 site:<id>（我的設備的範圍鍵），左樹節點是 node:<org>:<id>：同一個節點兩種鍵都算選到（第二輪 B2-02）
  const sel = props.selected === key || (it.kind === 'node' && !it.delegation && props.selected === 'site:' + it.id);
  const [menu, setMenu] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLSpanElement>(null);
  // Esc、點外面、切範圍都關（第二輪 B2-03）
  useDismiss(menu, () => setMenu(false), menuRef, [props.selected]);
  useEffect(() => {
    if (sel) rowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [sel]);
  const draggable = it.kind === 'node' || it.kind === 'home' || it.kind === 'delegation';
  const onDragStart = (e: DragEvent) => {
    e.dataTransfer.setData(DND_SCOPE, JSON.stringify({ key, label }));
    e.dataTransfer.effectAllowed = 'copy';
  };
  const dlgTip = it.delegation ? t('ot.dlg_tip', { from: it.delegation.from_org_name, until: it.delegation.expires_at ? dateStamp(it.delegation.expires_at) : t('ot.forever') }) : '';
  return (
    <li role="treeitem" aria-expanded={canOpen ? isOpen : undefined} aria-selected={sel} data-key={it.key}>
      <div
        ref={rowRef}
        className={'ot-row' + (sel ? ' sel' : '') + (ended ? ' ended' : '')}
        style={{ paddingLeft: depth * 14 }}
        data-name={name}
        data-scope={key}
        aria-current={sel ? 'true' : undefined}
      >
        {canOpen ? (
          <button className="ot-twisty" aria-label={isOpen ? t('ot.collapse') : t('ot.expand')} onClick={() => setOpen((s) => toggle(s, it.key))}>
            {isOpen ? '▾' : '▸'}
          </button>
        ) : (
          <span className="ot-twisty" />
        )}
        <button className="ot-name" disabled={ended || mfa} draggable={draggable && !ended && !mfa} onDragStart={onDragStart} onClick={() => props.pick(it, label)}>
          {it.kind === 'org' ? '▦ ' : ''}
          {name}
        </button>
        {it.level_label && <span className="ot-chip">{levelText(t, it.level_label)}</span>}
        {it.kind === 'node' && it.delegation && depth > 0 && (
          <span className="ot-dlg-mark" title={dlgTip} aria-label={dlgTip}>
            ⤳
          </span>
        )}
        {ended && <span className="badge">{t('ot.tag_ended')}</span>}
        <span className="spacer" />
        {it.device_count !== undefined && !ended && !mfa && (
          <span className="ot-count" title={t('ot.count_tip')}>
            {it.device_count}
          </span>
        )}
        {(it.kind === 'node' || it.kind === 'home') && (
          <span className="ot-menu-wrap" ref={menuRef}>
            <button className="icon-btn" aria-label={t('ot.more_actions', { name })} aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
              ⋯
            </button>
            {menu && (
              <span className="ot-menu" role="menu" onMouseLeave={() => setMenu(false)}>
                {it.kind === 'node' && (
                  <button role="menuitemcheckbox" aria-checked={!!props.subscribed(it)} onClick={() => (setMenu(false), void props.toggleSub(it))}>
                    {props.subscribed(it) ? '☑ ' : '☐ '}
                    {t('ot.notify_me_node')}
                  </button>
                )}
                <button role="menuitem" onClick={() => (setMenu(false), props.addTo('view', it))}>
                  {t('ot.add_to_view')}
                </button>
                <button role="menuitem" onClick={() => (setMenu(false), props.addTo('group', it))}>
                  {t('ot.add_to_group')}
                </button>
                <button role="menuitem" onClick={() => (setMenu(false), props.pick(it, label), nav('/live', { state: { fillScope: true } }))}>
                  {t('ot.open_in_wall')}
                </button>
              </span>
            )}
          </span>
        )}
      </div>
      {mfa && (
        <div className="ot-sub small ot-mfa" style={{ paddingLeft: depth * 14 + 22 }} data-testid="dlg-mfa">
          🔒 {t('ot.mfa_needed', { from: it.delegation?.from_org_name ?? '' })} <Link to="/settings">{t('ot.setup_mfa')}</Link>
        </div>
      )}
      {ended && (
        <div className="ot-sub small muted" style={{ paddingLeft: depth * 14 + 22 }} data-testid="dlg-ended">
          {t('ot.dlg_ended_line', { from: it.delegation?.from_org_name ?? it.name, time: it.ended_at ? stamp(it.ended_at) : '' })}
        </div>
      )}
      {isOpen && (
        <ul role="group">
          {props.extra.map((x) => (
            <Row key={x.key} {...props} it={{ ...x, _org: it.kind === 'org' ? it.id : it._org }} depth={depth + 1} extra={[]} parentPath={[...props.parentPath, name]} />
          ))}
          {it.kind === 'home' ? <HomeDevices it={it} depth={depth + 1} /> : <Children {...props} depth={depth + 1} parentPath={[...props.parentPath, name]} hasExtra={props.extra.length > 0} />}
        </ul>
      )}
    </li>
  );
}

function Children(props: Parameters<typeof Row>[0] & { hasExtra: boolean }) {
  const { t } = useTranslation();
  const { it } = props;
  const { orgs } = useScope();
  const owner = orgs.some((o) => o.org_id === it.id && o.role === 'owner');
  const q = useQuery({ queryKey: ['me-tree', 'level', it._api, it.key], queryFn: () => meTreeApi.level({ api: it._api }, it.key), staleTime: 30_000 });
  if (q.isLoading) return <li className="muted small" style={{ paddingLeft: props.depth * 14 + 22 }}>{t('common.loading')}</li>;
  if (q.isError) {
    // 翻頁時授權變了（409 AUTHORIZATION_CHANGED）：委派結束 / 縮小寫出委派方，其他照舊「權限已更新」並重列（SPEC #org-tree-api、ux-2-5）
    const e = q.error as { code?: string; detail?: Record<string, unknown> };
    const reason = String(e?.detail?.reason ?? '');
    const from = String(e?.detail?.from_org_name ?? '');
    const msg =
      reason === 'delegation_ended'
        ? t('ot.dlg_ended_short', { from })
        : reason === 'delegation_changed'
          ? t('ot.tree_dlg_changed', { from })
          : e?.code === 'AUTHORIZATION_CHANGED'
            ? t('ot.auth_changed')
            : t('common.error');
    return (
      <li className="small muted ot-empty" style={{ paddingLeft: props.depth * 14 + 22 }}>
        {msg}{' '}
        <button className="link-btn" onClick={() => void q.refetch()}>
          {t('common.retry')}
        </button>
      </li>
    );
  }
  const kids = (q.data ?? []).filter((x) => x.kind !== 'delegation' || x.parent_key === it.key);
  const org = it.kind === 'org' ? it.id : it._org;
  if (!kids.length && !props.hasExtra && it.kind === 'org' && it.org_kind !== 'personal' && !owner)
    return (
      <li className="muted small ot-empty" style={{ paddingLeft: props.depth * 14 + 22 }} data-testid="tree-org-empty">
        {t('ot.no_assignment_yet', { org: it.name })}
      </li>
    );
  return (
    <>
      {kids.map((x) => (
        <Row key={x.key} {...props} it={{ ...x, _api: it._api, _org: org, delegation: x.delegation ?? it.delegation ?? null }} extra={[]} />
      ))}
    </>
  );
}

/**
 * 家戶展開：列這一戶我看得到的設備（「我的設備」），可拖到影像牆格位。
 * NVR / Station / 基地台的通道縮排在 parent 底下、列名「CH1」，滑過與拖到牆上用完整名「NVR631 / CH1」；
 * 狀態點用通道自己的在線狀態（有影像牆資料時；SPEC #org-tree-ui 設備列，VMS UX 評審 2026-10-13 P0-7、P1-2）。
 */
function HomeDevices({ it, depth }: { it: Item; depth: number }) {
  const { t } = useTranslation();
  const { devices } = useScope();
  const { tree } = useDevices();
  const home = it.home_id ?? it.id;
  useLiveOfflineVersion(); // 牆上格子回報離線時重畫（同一個狀態來源，第二輪 B2-20）
  const list = devices.filter((d) => d.home_id === home && !d.container_only);
  if (!list.length) return <li className="muted small" style={{ paddingLeft: depth * 14 + 22 }}>{t('ot.no_devices')}</li>;
  const presenceOf = (ref: string, fallback: boolean) => {
    // 牆上有這一格就用格子算出的狀態（B3-07：切範圍後不在 tree 裡的通道不會退回 online 旗標變綠點）
    const ts = tileState(`${home}/${ref}`);
    if (ts && ts !== 'unknown') return camDot(ts) === 'online';
    const live = isLiveOffline(`${home}/${ref}`);
    for (const d of tree) {
      if (d.homeId !== home) continue;
      const c = d.aid === ref ? null : d.children.find((x) => x.aid === ref);
      if (d.aid !== ref && !c) continue;
      const st = c ? camState(c, d.presence, null, live) : camState(d, undefined, null, live);
      return st === 'unknown' ? fallback && !live : camDot(st) === 'online';
    }
    return fallback && !live;
  };
  // 名稱跟設備清單、地圖同一個（VmsDevice.name；第三輪 B3-08：樹寫「設備 VJW1」、清單寫「kinio-node-bl602」）
  const nameOf = (ref: string, fallback: string) => tree.find((x) => x.homeId === home && x.aid === ref)?.name || fallback;
  const rows = list.flatMap((d) => [
    { ref: d.device_ref, name: nameOf(d.device_ref, d.display_name), full: nameOf(d.device_ref, d.display_name), online: presenceOf(d.device_ref, d.online), child: false },
    // 通道依通道號排（第四輪 B4-16：NVR633 排成 CH2、CH1）
    ...[...(d.children ?? [])].sort((a, b) => chNo(a.device_ref) - chNo(b.device_ref) || a.device_ref.localeCompare(b.device_ref)).map((c) => {
      const name = c.display_name ?? c.device_ref;
      return { ref: c.device_ref, name, full: `${d.display_name} / ${name}`, online: presenceOf(c.device_ref, d.online), child: true };
    }),
  ]);
  return (
    <>
      {rows.map((d) => (
        <li key={d.ref} role="treeitem">
          <div
            className={'ot-row ot-device' + (d.child ? ' ot-channel' : '')}
            style={{ paddingLeft: (depth + (d.child ? 1 : 0)) * 14 }}
            draggable
            data-full-name={d.full}
            onDragStart={(e) => {
              e.dataTransfer.setData(DND_CAM, `${home}/${d.ref}`);
              e.dataTransfer.effectAllowed = 'move';
            }}
          >
            <span className="ot-twisty" />
            <button className="ot-name" onClick={() => placeCamera(`${home}/${d.ref}`)} title={`${d.full} · ${t('ot.place_on_wall')}`}>
              <span className={`dot ${d.online ? 'online' : 'offline'}`} />
              {d.name}
            </button>
          </div>
        </li>
      ))}
    </>
  );
}

/** 子設備 ref 的通道號（「<主機>:ch3」→ 3）；不是通道格式排最後。 */
function chNo(ref: string): number {
  const m = /:ch(\d+)$/i.exec(ref);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}
